import { API, postJSON } from "../api.js";
import { displaySettingHtml, wireDisplaySetting } from "./display.js";
import { openResetSaveModal } from "./onboarding.js";
import { enterDashboard } from "./shell.js";
import { $, esc, fmtMoney } from "../ui/format.js";
import { openInvestSliderModal, toast } from "../ui/modals.js";

// ---------------------------------------------------------------- commands (Overview bar)

// Quick-pick helper: builds 25/50/75/Max buttons scaled to a cap.
function pctPicks(cap) {
  return [
    { label: "25%", value: Math.round(cap * 0.25) },
    { label: "50%", value: Math.round(cap * 0.5) },
    { label: "75%", value: Math.round(cap * 0.75) },
    { label: "Max", value: Math.round(cap) },
  ];
}

const COMMAND_DEFS = [
  {
    id: "raise", label: "Raise Capital", path: "/commands/raise-capital",
    description: "New equity capital dilutes founder ownership proportionally — the bigger the raise relative to your current valuation, the bigger the slice investors take.",
    sliderMax: (s) => Math.max(500000, (s.ratios.valuation || 500000) * 0.6),
    default: 250000,
    capLabel: (s) => `Valuation ${fmtMoney(s.ratios.valuation)}`,
    computeEffect: (amount, s) => {
      const preMoney = s.ratios.valuation || 0;
      const sentiment = s.ratios.investor_sentiment !== undefined ? s.ratios.investor_sentiment : 60;
      const sentimentDiscount = Math.max(0.75, Math.min(1.15, 1 + (60 - sentiment) / 400));
      const effectivePreMoney = preMoney / sentimentDiscount;
      const postMoney = effectivePreMoney + amount;
      const dilutionFactor = postMoney > 0 ? effectivePreMoney / postMoney : 1;
      const currentOwnership = s.ratios.founder_ownership_pct !== undefined ? s.ratios.founder_ownership_pct : 100;
      const newOwnership = currentOwnership * dilutionFactor;
      return {
        stats: [
          { label: "Cash Raised", value: fmtMoney(amount), cls: "pos" },
          { label: "Founder Ownership", value: `${currentOwnership.toFixed(1)}% → ${newOwnership.toFixed(1)}%`, cls: newOwnership < currentOwnership ? "neg" : "" },
          { label: "Dilution", value: `-${(currentOwnership - newOwnership).toFixed(1)} pts`, cls: "neg" },
        ],
        note: sentimentDiscount > 1.02
          ? "Investor sentiment is soft right now — this raise costs extra dilution on these terms."
          : "Investor sentiment is healthy — standard terms.",
        noteWarn: sentimentDiscount > 1.05,
      };
    },
  },
  {
    id: "loan", label: "Take Loan", path: "/commands/take-loan",
    description: "Auto-shopped to the cheapest eligible bank at your current leverage — see the Banking & Debt tab for named quotes and rates.",
    sliderMax: (s) => Math.max(200000, (s.balance_sheet.CASH || 0) * 3 + 500000),
    default: 150000,
    capLabel: (s) => `Debt/Equity ${s.ratios.debt_to_equity ?? "0"}x`,
    computeEffect: (amount, s) => {
      const rating = s.ratios.credit_rating || "B";
      const estRate = rating.startsWith("A") ? 0.06 : rating.startsWith("B") ? 0.09 : 0.13;
      return {
        stats: [
          { label: "Cash Drawn", value: fmtMoney(amount), cls: "pos" },
          { label: "Est. Annual Interest", value: fmtMoney(amount * estRate), cls: "neg" },
          { label: "Current Rating", value: rating },
        ],
        note: "Larger draws relative to equity push your leverage up, which can raise the rate on your NEXT loan.",
      };
    },
  },
  {
    id: "paydebt", label: "Pay Down Debt", path: "/commands/pay-down-debt",
    description: "Pays off your highest-interest-rate loan first — the financially sensible order.",
    sliderMax: (s) => s.balance_sheet.CASH || 0,
    default: 50000,
    capLabel: (s) => `Cash ${fmtMoney(s.balance_sheet.CASH)}`,
    computeEffect: (amount, s) => ({
      stats: [
        { label: "Debt Repaid", value: fmtMoney(amount), cls: "pos" },
        { label: "Cash Remaining", value: fmtMoney((s.balance_sheet.CASH || 0) - amount) },
        { label: "Interest Coverage", value: `${s.ratios.interest_coverage ?? "n/a"} (improves)`, cls: "pos" },
      ],
      note: "Reducing debt lowers future interest expense and improves your leverage ratios.",
    }),
  },
  {
    id: "dividend", label: "Issue Dividend", path: "/commands/issue-dividend",
    description: "Paid out of retained earnings, capped by available cash.",
    sliderMax: (s) => Math.max(0, Math.min(s.balance_sheet.RETAINED_EARNINGS || 0, s.balance_sheet.CASH || 0)),
    default: 25000,
    capLabel: (s) => `Retained earnings ${fmtMoney(s.balance_sheet.RETAINED_EARNINGS)}`,
    computeEffect: (amount, s) => ({
      stats: [
        { label: "Dividend Paid", value: fmtMoney(amount), cls: "neg" },
        { label: "Cash After", value: fmtMoney((s.balance_sheet.CASH || 0) - amount) },
        { label: "Retained Earnings After", value: fmtMoney((s.balance_sheet.RETAINED_EARNINGS || 0) - amount) },
      ],
      note: "Cash paid to shareholders can't be reinvested in growth this month.",
    }),
  },
  {
    id: "capex", label: "Buy Equipment", path: "/commands/buy-equipment",
    description: "Capital expenditure on equipment/facilities — capitalized as PP&E and depreciated straight-line over 5 years.",
    sliderMax: (s) => s.balance_sheet.CASH || 0,
    default: 75000,
    capLabel: (s) => `Cash ${fmtMoney(s.balance_sheet.CASH)}`,
    computeEffect: (amount) => ({
      stats: [
        { label: "PP&E Added", value: fmtMoney(amount), cls: "pos" },
        { label: "Monthly Depreciation", value: fmtMoney(amount / 60), cls: "neg" },
      ],
      note: "Straight-line over 5 years (60 months) — a small, permanent hit to monthly operating expense.",
    }),
  },
  {
    id: "marketing", label: "Marketing Campaign", path: "/commands/marketing-campaign",
    description: "One-time S&M spend that buys a temporary growth boost and a small permanent brand bump.",
    sliderMax: (s) => s.balance_sheet.CASH || 0,
    default: 50000,
    capLabel: (s) => `Cash ${fmtMoney(s.balance_sheet.CASH)}`,
    computeEffect: (amount, s) => {
      const boost = Math.min(0.04, amount / 2_000_000);
      const projectedRevenue = (s.income_statement.revenue || 0) * (1 + boost);
      return {
        stats: [
          { label: "Growth Boost", value: `+${(boost * 100).toFixed(2)}%/mo`, cls: "pos" },
          { label: "Duration", value: "3 months" },
          { label: "Brand Score", value: `${s.ratios.brand_score} → ${Math.min(100, s.ratios.brand_score + 1.5).toFixed(1)}`, cls: "pos" },
          { label: "Next-Month Revenue (est.)", value: fmtMoney(projectedRevenue) },
        ],
        note: boost >= 0.04 ? "You've hit the maximum growth boost for a single campaign — additional spend beyond this point is wasted." : null,
        noteWarn: boost >= 0.04,
      };
    },
  },
  {
    id: "rnd", label: "Invest in R&D", path: "/commands/invest-rnd",
    description: "One-time capitalized R&D spend that buys a longer-lasting growth boost than marketing, but takes longer to show up.",
    sliderMax: (s) => s.balance_sheet.CASH || 0,
    default: 100000,
    capLabel: (s) => `Cash ${fmtMoney(s.balance_sheet.CASH)}`,
    computeEffect: (amount) => {
      const boost = Math.min(0.03, amount / 3_000_000);
      return {
        stats: [
          { label: "Growth Boost", value: `+${(boost * 100).toFixed(2)}%/mo`, cls: "pos" },
          { label: "Duration", value: "6 months" },
        ],
        note: boost >= 0.03 ? "You've hit the maximum growth boost for a single R&D investment — additional spend beyond this point is wasted." : null,
        noteWarn: boost >= 0.03,
      };
    },
  },
];

function wireCommandButton(btnEl, cmdId) {
  const def = COMMAND_DEFS.find((c) => c.id === cmdId);
  if (!btnEl || !def) return;
  btnEl.addEventListener("click", () => {
    const s = window.__snapshot;
    const max = def.sliderMax(s);
    openInvestSliderModal({
      title: def.label,
      description: def.description,
      min: 0,
      max,
      step: Math.max(1, Math.round(max / 200)),
      default: Math.min(def.default, max),
      capLabel: def.capLabel ? def.capLabel(s) : undefined,
      quickPicks: pctPicks(max),
      computeEffect: (amount) => def.computeEffect(amount, s),
      confirmLabel: def.label,
      onConfirm: async (amount) => {
        const res = await postJSON(def.path, { amount });
        await window.refreshAll();
        toast(res.message);
      },
    });
  });
}

function wireCommands() {
  $("btn-next-month").addEventListener("click", nextMonth);
  $("btn-fast-forward").addEventListener("click", (e) => {
    e.stopPropagation();
    $("ff-menu").classList.toggle("open");
  });
  document.querySelectorAll("#ff-menu [data-ff]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      $("ff-menu").classList.remove("open");
      fastForward(parseInt(btn.dataset.ff));
    });
  });
  document.addEventListener("click", () => $("ff-menu") && $("ff-menu").classList.remove("open"));
  $("btn-save-load").addEventListener("click", openSaveLoadModal);
}

/** The Demo company (2.7): a ready-made company three years in (backend/demo, POST /api/demo/load). */
async function loadDemoCompany() {
  try {
    const r = await postJSON("/demo/load", {});
    $("onboarding-screen").classList.add("hidden");
    await enterDashboard();
    toast(r.message || "Demo company loaded.", false, 7000);
  } catch (e) {
    toast(`Couldn't open the demo: ${e.message || e}`, true);
  }
}
window.loadDemoCompany = loadDemoCompany;

async function openSaveLoadModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal" style="width:min(560px, 92vw); max-height:82vh; display:flex; flex-direction:column;">
      <h3>Save / Load Game</h3>
      <div class="field">
        <label>Save current game as</label>
        <div style="display:flex; gap:8px;">
          <input id="save-slot-name" placeholder="e.g. Before the IPO push" style="flex:1;">
          <button class="primary" id="save-slot-btn">Save</button>
        </div>
      </div>
      <div id="save-slot-list" style="overflow-y:auto; flex:1; margin:14px 0;">Loading…</div>
      <div class="modal-actions" style="justify-content:space-between; flex-wrap:wrap; gap:8px;">
        <div style="display:flex; gap:8px;">
          <button id="export-game-btn">⬇ Export to File</button>
          <label style="display:inline-block;">
            <input type="file" id="import-game-input" accept=".db" style="display:none;">
            <button id="import-game-btn" type="button">⬆ Import from File</button>
          </label>
        </div>
        <button id="save-load-close">Close</button>
      </div>
      ${displaySettingHtml()}
      <div class="demo-zone">
        <div><b>🎬 Demo company</b><span>A company three years in, with every section in use: for showing the game to someone. It replaces the game in progress.</span></div>
        <button id="load-demo-btn">Open the demo</button>
      </div>
      <div class="danger-zone">
        <div><b>Reset save</b><span>Delete this company and start again from founding. Named save slots above are kept.</span></div>
        <button id="reset-save-btn" class="danger">Reset save</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  wireDisplaySetting(backdrop);
  backdrop.querySelector("#save-load-close").addEventListener("click", () => backdrop.remove());
  backdrop.querySelector("#reset-save-btn").addEventListener("click", () => { backdrop.remove(); openResetSaveModal(); });
  backdrop.querySelector("#load-demo-btn").addEventListener("click", async () => {
    if (!confirm("Open the demo company? The game in progress will be replaced (save it to a slot first if you want to keep it).")) return;
    backdrop.remove();
    await loadDemoCompany();
  });
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) backdrop.remove(); });

  async function refreshSaveList() {
    const listEl = backdrop.querySelector("#save-slot-list");
    const saves = await fetch(`${API}/saves`).then((x) => x.json());
    if (!saves.length) {
      listEl.innerHTML = `<p style="color:var(--text-mid); font-size:12.5px;">No saves yet.</p>`;
      return;
    }
    listEl.innerHTML = saves.map((s) => `
      <div style="display:flex; align-items:center; justify-content:space-between; padding:9px 0; border-bottom:1px solid var(--line);">
        <div>
          <div style="font-size:13.5px;">${esc(s.slot_name)}</div>
          <div style="font-size:11px; color:var(--text-low);">${esc(s.company_name || "—")} · Month ${s.month ?? "—"} · ${new Date(s.modified_at * 1000).toLocaleString()}</div>
        </div>
        <div style="display:flex; gap:6px;">
          <button data-load-slot="${esc(s.slot_name)}" style="font-size:12px; padding:5px 10px;">Load</button>
          <button data-delete-slot="${esc(s.slot_name)}" style="font-size:12px; padding:5px 10px;">Delete</button>
        </div>
      </div>
    `).join("");
    listEl.querySelectorAll("[data-load-slot]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!confirm(`Load '${btn.dataset.loadSlot}'? Your current unsaved progress will be replaced.`)) return;
        await postJSON(`/saves/${encodeURIComponent(btn.dataset.loadSlot)}/load`, {});
        backdrop.remove();
        await enterDashboard();
      });
    });
    listEl.querySelectorAll("[data-delete-slot]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!confirm(`Delete save '${btn.dataset.deleteSlot}'? This cannot be undone.`)) return;
        await fetch(`${API}/saves/${encodeURIComponent(btn.dataset.deleteSlot)}`, { method: "DELETE" });
        refreshSaveList();
      });
    });
  }
  await refreshSaveList();

  backdrop.querySelector("#save-slot-btn").addEventListener("click", async () => {
    const name = backdrop.querySelector("#save-slot-name").value.trim();
    if (!name) return;
    try {
      await postJSON(`/saves/${encodeURIComponent(name)}`, {});
      backdrop.querySelector("#save-slot-name").value = "";
      toast(`Saved '${name}'.`);
      refreshSaveList();
    } catch (e) {
      toast(e.message || "Could not save.");
    }
  });

  backdrop.querySelector("#export-game-btn").addEventListener("click", () => {
    window.location.href = `${API}/export`;
  });

  backdrop.querySelector("#import-game-btn").addEventListener("click", () => {
    backdrop.querySelector("#import-game-input").click();
  });
  backdrop.querySelector("#import-game-input").addEventListener("change", async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!confirm("Importing will replace your current game entirely. Continue?")) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API}/import`, { method: "POST", body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed.");
      backdrop.remove();
      await enterDashboard();
    } catch (err) {
      toast(err.message || "Could not import that file.");
    }
  });
}

async function nextMonth() {
  $("btn-next-month").disabled = true;
  try {
    // a government payment that would be missed asks first (app/reminders.js)
    if (window.gesBeforeMonth && !(await window.gesBeforeMonth())) return;
    const before = window.__snapshot;
    const res = await postJSON("/command", { text: "end month", quick: true });
    await window.refreshAll();
    // ceo.js replaces the monthly wall-of-text with a short month-close summary
    if (typeof window.showMonthClose === "function") window.showMonthClose(res.narrative, before);
    else showReportModal(res.narrative);
  } catch (e) {
    toast("Error: " + e.message);
  } finally {
    $("btn-next-month").disabled = false;
  }
}

const MAJOR_EVENT_LABEL = {
  game_over: (p) => `💀 Game over: ${p.reason}`,
  board_vote_no_confidence: (p) => `⚠ Board voted no confidence — forced ${fmtMoney(p.forced_dividend)} dividend`,
  board_intervention: (p) => `⚠ Board mandated cost discipline (${(p.cost_cut_pct * 100).toFixed(0)}% cuts)`,
  risk_incident: (p) => `⚠ ${p.description}`,
  executive_poached: (p) => `⚠ ${p.name} (${p.role}) poached by ${p.poached_by}`,
  ipo_completed: (p) => `🔔 IPO completed — raised ${fmtMoney(p.primary_raise)}`,
  insolvency_warning: () => `⚠ Six months of deep insolvency — this would end the game on Ironman`,
};

async function fastForward(months) {
  $("btn-next-month").disabled = true;
  $("btn-fast-forward").disabled = true;
  $("btn-fast-forward").textContent = `Skipping ${months}mo…`;
  try {
    if (window.gesBeforeMonth && !(await window.gesBeforeMonth())) return;
    const before = window.__snapshot;
    const res = await postJSON("/turn/fast-forward", { months });
    await window.refreshAll();
    showFastForwardModal(res, before);
  } catch (e) {
    toast("Error: " + e.message);
  } finally {
    $("btn-next-month").disabled = false;
    $("btn-fast-forward").disabled = false;
    $("btn-fast-forward").textContent = "Fast Forward ▾";
  }
}

function showFastForwardModal(res, before) {
  // cash, revenue and valuation across the whole skip, with the valuation's "Why?" (views/ceo.js)
  const snap = window.__snapshot;
  const change = snap && res.months_advanced && window.gesChangeGridHtml ? window.gesChangeGridHtml(snap, before) : "";
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  const eventsHtml = res.major_events.length
    ? res.major_events.map((e) => `<div class="ff-event">M${e.month} — ${MAJOR_EVENT_LABEL[e.type] ? MAJOR_EVENT_LABEL[e.type](e.payload) : e.type}</div>`).join("")
    : `<p style="color:var(--text-mid); font-size:13px;">Nothing dramatic happened — steady sailing.</p>`;
  backdrop.innerHTML = `
    <div class="modal month-close" style="width:min(640px, 92vw);">
      <h3>Skipped ${res.months_advanced} month${res.months_advanced === 1 ? "" : "s"}</h3>
      ${res.stopped_early_reason ? `<p style="color:var(--oxblood); font-size:13px; margin-top:-8px;">Stopped early: ${esc(res.stopped_early_reason)}</p>` : ""}
      ${change ? `<div class="mc-label" style="margin-bottom:6px;">Since month ${before ? before.month : "?"}</div>${change}` : ""}
      <div style="max-height:280px; overflow-y:auto; margin:10px 0 18px;">${eventsHtml}</div>
      <div class="modal-actions"><button class="primary" id="ff-close">Continue</button></div>
    </div>
  `;
  document.body.appendChild(backdrop);
  if (change && window.gesWireChangeGrid) window.gesWireChangeGrid(backdrop);
  const done = () => { backdrop.remove(); if (window.showQuarterDividend) window.showQuarterDividend(); };
  backdrop.querySelector("#ff-close").addEventListener("click", done);
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) done(); });
}

function showReportModal(narrative) {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal" style="width:min(680px, 90vw); max-height:80vh; display:flex; flex-direction:column;">
      <h3>Board Report</h3>
      <div class="report-narrative report" style="overflow-y:auto; flex:1; margin-bottom:16px;">${esc(narrative)}</div>
      <div class="modal-actions">
        <button class="primary" id="modal-ok">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  const close = () => backdrop.remove();
  backdrop.querySelector("#modal-ok").addEventListener("click", close);
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
}

export { showReportModal, wireCommandButton, wireCommands };
